import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Copy, ExternalLink, Share2 } from "lucide-react";
import { Orb } from "@/components/Orb";
import { PfizerLogo } from "@/components/PfizerLogo";
import { TeammateCard } from "@/components/TeammateCard";
import { TryItNow } from "@/components/TryItNow";
import { createTeammate } from "@/lib/api";
import { chatgptUrl } from "@/lib/chatgptLink";
import type { Teammate } from "@/lib/types";
import { cn } from "@/lib/cn";

type Step = "role" | "task" | "inputs" | "review" | "generating" | "result";

const ROLE_SUGGESTIONS = [
  "Sales rep",
  "Marketing manager",
  "Finance analyst",
  "Operations lead",
  "Customer success",
  "Product manager",
  "Executive assistant",
  "Researcher",
  "HR partner",
];

const TASK_SUGGESTIONS = [
  "Prep for client meetings",
  "Summarise long reports",
  "Draft follow-up emails",
  "Reconcile monthly expenses",
  "Turn notes into action items",
  "Compare policy documents",
  "Build campaign briefs",
  "Answer repetitive questions",
];

export default function Build() {
  const [params] = useSearchParams();
  const sessionId = Number(params.get("s") || "1");

  const [step, setStep] = useState<Step>("role");
  const [creatorName, setCreatorName] = useState(() => localStorage.getItem("pfizer_ai_name") ?? "");
  const [role, setRole] = useState("");
  const [task, setTask] = useState("");
  const [inputs, setInputs] = useState("");
  const [humanReview, setHumanReview] = useState("");
  const [teammate, setTeammate] = useState<Teammate | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (creatorName) localStorage.setItem("pfizer_ai_name", creatorName);
  }, [creatorName]);

  const canNextRole = role.trim().length >= 2;
  const canNextTask = task.trim().length >= 5;
  const canNextInputs = inputs.trim().length >= 3;
  const canGenerate = humanReview.trim().length >= 3;

  async function generate() {
    setStep("generating");
    setError(null);

    // client-side jitter (0-400ms) to spread the simultaneous-tap spike
    // across ~0.5s. Hidden entirely inside the orb animation.
    const jitter = Math.random() * 400;
    const minDisplay = 1800;
    const started = Date.now();

    try {
      await new Promise((r) => setTimeout(r, jitter));
      const t = await createTeammate({ sessionId, role, task, inputs, humanReview, creatorName });
      // enforce minimum orb display so fast responses don't feel jumpy
      // and slow ones feel intentional
      const elapsed = Date.now() - started;
      if (elapsed < minDisplay) {
        await new Promise((r) => setTimeout(r, minDisplay - elapsed));
      }
      setTeammate(t);
      setStep("result");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
      setStep("review");
    }
  }

  return (
    <div className="min-h-dvh">
      <div className="mx-auto flex min-h-dvh max-w-xl flex-col px-5 pb-10 pt-6">
        <Header step={step} sessionId={sessionId} />

        <div className="relative mt-6 flex flex-1 flex-col">
          <AnimatePresence mode="wait">
            {step === "role" && (
              <StepCard
                key="role"
                title="What's your role?"
                hint="One line — pick a suggestion or type your own."
              >
                <input
                  className="input-glass"
                  placeholder="e.g. Field sales rep"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  autoFocus
                />
                <Chips
                  options={ROLE_SUGGESTIONS}
                  selected={role}
                  onSelect={setRole}
                />
                <div className="mt-2 rounded-lg p-[1px]" style={{ background: "linear-gradient(135deg, rgba(94,234,255,0.25), rgba(28,127,255,0.08))" }}>
                  <div className="rounded-[14px] bg-ink-950/50 px-4 py-3">
                    <label className="block text-[10px] uppercase tracking-widest text-white/50">
                      Your first name <span className="opacity-50">(optional — shows as "by you" on the board)</span>
                    </label>
                    <input
                      className="w-full bg-transparent py-1 text-base text-white placeholder-white/30 focus:outline-none"
                      placeholder="e.g. Rui"
                      value={creatorName}
                      onChange={(e) => setCreatorName(e.target.value.slice(0, 30))}
                      autoComplete="given-name"
                    />
                  </div>
                </div>
                <NextBar onNext={() => setStep("task")} disabled={!canNextRole} />
              </StepCard>
            )}

            {step === "task" && (
              <StepCard
                key="task"
                title="What task eats your time?"
                hint="Something repetitive, frequent, and a bit boring."
                onBack={() => setStep("role")}
              >
                <textarea
                  className="input-glass min-h-[110px] resize-none"
                  placeholder="e.g. I prep a sales brief before every client meeting"
                  value={task}
                  onChange={(e) => setTask(e.target.value)}
                  rows={3}
                  autoFocus
                />
                <Chips
                  options={TASK_SUGGESTIONS}
                  selected={task}
                  onSelect={setTask}
                />
                <NextBar onNext={() => setStep("inputs")} disabled={!canNextTask} />
              </StepCard>
            )}

            {step === "inputs" && (
              <StepCard
                key="inputs"
                title="What context does it need?"
                hint="Documents, data, templates, past examples."
                onBack={() => setStep("task")}
              >
                <textarea
                  className="input-glass min-h-[110px] resize-none"
                  placeholder="e.g. CRM notes, last 3 emails, product deck, pricing sheet"
                  value={inputs}
                  onChange={(e) => setInputs(e.target.value)}
                  rows={3}
                  autoFocus
                />
                <NextBar onNext={() => setStep("review")} disabled={!canNextInputs} />
              </StepCard>
            )}

            {step === "review" && (
              <StepCard
                key="review"
                title="What must stay with you?"
                hint="The final judgment calls you'll never delegate."
                onBack={() => setStep("inputs")}
              >
                <textarea
                  className="input-glass min-h-[110px] resize-none"
                  placeholder="e.g. final tone, pricing decisions, sensitive relationships"
                  value={humanReview}
                  onChange={(e) => setHumanReview(e.target.value)}
                  rows={3}
                  autoFocus
                />
                {error && (
                  <div className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200">
                    {error}
                  </div>
                )}
                <NextBar onNext={generate} disabled={!canGenerate} label="Build my teammate" />
              </StepCard>
            )}

            {step === "generating" && (
              <Generating key="generating" />
            )}

            {step === "result" && teammate && (
              <Result key="result" teammate={teammate} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function Header({ step, sessionId }: { step: Step; sessionId: number }) {
  const progress =
    step === "role" ? 0.1 :
    step === "task" ? 0.3 :
    step === "inputs" ? 0.55 :
    step === "review" ? 0.8 :
    step === "generating" ? 0.95 : 1;

  return (
    <div className="flex items-center gap-3">
      <PfizerLogo className="h-6" />
      <div className="h-4 w-px bg-white/10" />
      <div className="text-xs uppercase tracking-widest text-white/60">AI Teammate</div>
      <div className="ml-auto text-xs text-white/40">S{sessionId}</div>
      <div className="h-1.5 w-full max-w-[80px] overflow-hidden rounded-full bg-white/5">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-cyan-glow to-brand-500"
          initial={{ width: 0 }}
          animate={{ width: `${progress * 100}%` }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </div>
  );
}

function StepCard({
  title,
  hint,
  children,
  onBack,
}: {
  title: string;
  hint: string;
  children: React.ReactNode;
  onBack?: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -16 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-1 flex-col"
    >
      {onBack && (
        <button
          className="mb-2 inline-flex items-center gap-1.5 self-start text-xs text-white/50 hover:text-white"
          onClick={onBack}
        >
          <ArrowLeft className="h-3 w-3" /> back
        </button>
      )}
      <h2 className="font-display text-4xl leading-[1.05] text-white">{title}</h2>
      <p className="mt-2 text-sm text-white/60">{hint}</p>
      <div className="mt-6 flex flex-col gap-3">{children}</div>
    </motion.div>
  );
}

function Chips({
  options,
  selected,
  onSelect,
}: {
  options: string[];
  selected: string;
  onSelect: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.trim().toLowerCase() === opt.toLowerCase();
        return (
          <button
            key={opt}
            onClick={() => onSelect(opt)}
            className={cn(
              "rounded-full px-3 py-1.5 text-xs transition",
              active
                ? "bg-white text-ink-900"
                : "border border-white/10 bg-white/5 text-white/70 hover:bg-white/10",
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function NextBar({
  onNext,
  disabled,
  label = "Continue",
}: {
  onNext: () => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button className="btn-primary mt-2 w-full" onClick={onNext} disabled={disabled}>
      {label} <ArrowRight className="h-4 w-4" />
    </button>
  );
}

function Generating() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-1 flex-col items-center justify-center text-center"
    >
      <Orb size={220} intensity="thinking" />
      <motion.div
        className="mt-8 max-w-xs"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <p className="font-display text-3xl leading-tight text-white">
          Designing your teammate…
        </p>
        <RotatingHints />
      </motion.div>
    </motion.div>
  );
}

const HINTS = [
  "Giving it a name and mission",
  "Choosing what it handles alone",
  "Writing your reusable prompt",
  "Mapping a 2-week plan",
];

function RotatingHints() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % HINTS.length), 1700);
    return () => clearInterval(t);
  }, []);
  return (
    <AnimatePresence mode="wait">
      <motion.p
        key={i}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.4 }}
        className="mt-3 text-sm text-white/60"
      >
        {HINTS[i]}
      </motion.p>
    </AnimatePresence>
  );
}

function Result({ teammate }: { teammate: Teammate }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/t/${teammate.id}`;

  async function copyPrompt() {
    await navigator.clipboard.writeText(teammate.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  async function share() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Meet ${teammate.name}`,
          text: teammate.mission,
          url: shareUrl,
        });
      } catch {
        /* dismissed */
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-1 flex-col"
    >
      <div className="text-center">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-cyan-glow/10 px-3 py-1 text-[10px] uppercase tracking-widest text-cyan-glow">
          <Check className="h-3 w-3" /> Teammate ready
        </div>
        <h2 className="mt-3 font-display text-4xl leading-tight text-white">
          Meet <span className="text-cyan-glow">{teammate.name}</span>
        </h2>
      </div>

      <TeammateCard teammate={teammate} className="mt-6" />

      <TryItNow teammate={teammate} />

      <div className="mt-5 grid grid-cols-2 gap-3">
        <button className="btn-ghost" onClick={copyPrompt}>
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy prompt"}
        </button>
        <a
          className="btn-ghost"
          href={chatgptUrl(teammate)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLink className="h-4 w-4" />
          Open in ChatGPT
        </a>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3">
        <button className="btn-ghost" onClick={share}>
          <Share2 className="h-4 w-4" />
          Share with a colleague
        </button>
      </div>

      <div className="glass mt-5 rounded-lg p-4 text-sm text-white/80">
        <div className="mb-1 text-[10px] uppercase tracking-widest text-cyan-glow">
          Your 2-week commitment
        </div>
        Try this teammate on your next 3 real tasks. Refine the prompt each time.
      </div>
    </motion.div>
  );
}
